/**
 * Stacked-area chart of active users by subscription tier over time.
 * Uses the tier-specific palette from theme.ts so colors match everywhere
 * the Revenue tab shows tiers.
 */
import React, { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TIER_COLORS, TIER_DISPLAY } from '../theme';
import { formatCompact, formatInt } from '../format';

export interface TierLadderPoint {
  day: string;
  trial: number;
  basic: number;
  full: number;
  extended: number;
}

export interface TierLadderChartProps {
  series: TierLadderPoint[];
  height?: number;
}

const TIER_ORDER: Array<keyof Omit<TierLadderPoint, 'day'>> = [
  'trial',
  'basic',
  'full',
  'extended',
];

export const TierLadderChart: React.FC<TierLadderChartProps> = ({ series, height = 280 }) => {
  const totalCurrent = useMemo(() => {
    if (!series.length) return 0;
    const last = series[series.length - 1];
    return last.trial + last.basic + last.full + last.extended;
  }, [series]);

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[11px] text-gray-500">
          {totalCurrent > 0 ? (
            <>
              <span className="font-semibold text-gray-900">{formatInt(totalCurrent)}</span>{' '}
              active users at end of range
            </>
          ) : (
            'Active by tier over time'
          )}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            {TIER_ORDER.map((t) => (
              <linearGradient key={t} id={`tier-grad-${t}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={TIER_COLORS[t]} stopOpacity={0.85} />
                <stop offset="100%" stopColor={TIER_COLORS[t]} stopOpacity={0.25} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-muted)" vertical={false} />
          <XAxis
            dataKey="day"
            tick={{ fontSize: 11, fill: 'var(--text-secondary)', fontFamily: 'Inter, system-ui, sans-serif' }}
            tickLine={false}
            axisLine={{ stroke: 'var(--border)' }}
            minTickGap={32}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--text-secondary)', fontFamily: 'Inter, system-ui, sans-serif' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => formatCompact(Number(v))}
            width={44}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            }}
            labelStyle={{ fontWeight: 600, color: 'var(--text-primary)' }}
            formatter={(value, name) => [formatInt(Number(value)), TIER_DISPLAY[String(name)] ?? name]}
          />
          <Legend
            iconType="circle"
            wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
            formatter={(v) => <span className="text-gray-600">{TIER_DISPLAY[String(v)] ?? v}</span>}
          />
          {TIER_ORDER.map((t) => (
            <Area
              key={t}
              type="monotone"
              dataKey={t}
              stackId="tier"
              stroke={TIER_COLORS[t]}
              strokeWidth={1.5}
              fill={`url(#tier-grad-${t})`}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
